"use client";

import { useCallback, useState } from "react";
import log from "logbeacon";
import { safeStringifyError } from "utils";
import { OspToast } from "@ospweb/sdk-ui";
import type {
  InteractiveToolInfo,
  ConfirmSharpenScoreSubmitPayload,
} from "@/src/components/Chat/InteractiveTool/types";
import { trackEvent } from "@/src/utils/tracking";

export function useSharpenConfirmSubmit(options: {
  toolInfo?: InteractiveToolInfo;
  chatId?: string;
  submitInteractiveToolMessage: (content: string) => void | Promise<void>;
}): { confirm: () => Promise<void>; isSubmitting: boolean } {
  const { toolInfo, chatId, submitInteractiveToolMessage } = options;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const confirm = useCallback(async () => {
    if (!toolInfo || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    trackEvent("ff_update_confirm", { chat_id: chatId });

    const payload: ConfirmSharpenScoreSubmitPayload = { confirmed: true };

    try {
      await submitInteractiveToolMessage(JSON.stringify(payload));
    } catch (error) {
      log.error(
        "[useSharpenConfirmSubmit] submit failed",
        safeStringifyError(error),
      );
      OspToast.error({
        content: "Something went wrong. Please try again.",
        key: "sharpen-confirm-submit-failed",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [chatId, isSubmitting, submitInteractiveToolMessage, toolInfo]);

  return { confirm, isSubmitting };
}
